/**
 * navLogic.js — the arithmetic behind the NAV / Performance page
 * ==============================================================
 * Everything here is a pure function of the two payloads the page reads, so
 * each rule can be pinned by a test without drawing anything. The components
 * in this folder only lay the answers out.
 */
import { money, toNumber } from "../../components/format";
import { groupKey, usdValue } from "../../components/positionRollup";

/* ── Dates ─────────────────────────────────────────────────────────── */

const utc = (iso) => new Date(`${String(iso).slice(0, 10)}T00:00:00Z`);
const iso = (d) => d.toISOString().slice(0, 10);

/**
 * The last day of the month before the one `day` falls in. A month end picks
 * the month before it, so 31 October compares with 30 September and never
 * with itself.
 */
export function previousMonthEnd(day) {
  const d = utc(day);
  return iso(new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 0)));
}

/** 31 December of the year before `day`. */
export function previousYearEnd(day) {
  return `${utc(day).getUTCFullYear() - 1}-12-31`;
}

/* ── Performance windows ───────────────────────────────────────────── */

export const WINDOW_TILES = [
  ["mtd", "Month to date"],
  ["ytd", "Year to date"],
  ["one_year", "Last 12 months"],
  ["three_year", "Last 3 years"],
  ["itd", "Since inception"],
];

const SAME_FIELDS = ["opening_usd", "closing_usd", "change_usd", "net_income_usd", "capital_and_other_usd"];

/**
 * Whether two windows report the same thing. The fund is younger than three
 * years, so the three-year window opens at inception and repeats the
 * since-inception tile figure for figure; the page shows one of them.
 */
export function sameWindow(a, b) {
  if (!a || !b) return false;
  return SAME_FIELDS.every((k) => toNumber(a[k]) === toNumber(b[k]));
}

/**
 * An estimated return for one window, or why there is none.
 *
 * CLAUDE.md §0.31: a change in net assets is not a return, because capital
 * in and out is in it. So this takes the capital out and divides what is left
 * by the average capital employed — the Modified Dietz estimate, with the
 * period's net capital assumed to arrive halfway through:
 *
 *     gain    = change in net assets − capital in less out
 *             = profit + currency translation
 *     average = opening net assets + ½ × capital in less out
 *     return  = gain ÷ average
 *
 * A server that predates the capital / currency split reports only the
 * remainder (`capital_and_other_usd`, which has the currency move in it), and
 * then the gain is profit alone and the remainder is the flow. Which of the
 * two was used is returned as `basis`, so the explanation beside the figure
 * can say so rather than describe the other one (§0.17).
 *
 * The average can be nil or below — on this book, distributions have paid out
 * more than the fund held at the start of most windows — and then there is no
 * return to state. `value` is null and `reason` says why in figures (§0.30:
 * n/a, never 0.00).
 */
export function windowReturn(w) {
  if (!w) return { value: null, reason: "No figures for this period." };
  const opening = toNumber(w.opening_usd);
  const change = toNumber(w.change_usd);
  const capital = toNumber(w.capital_usd);
  let gain;
  let flows;
  let basis;
  if (capital !== null && change !== null) {
    gain = change - capital;
    flows = capital;
    basis = "split";
  } else {
    gain = toNumber(w.net_income_usd);
    flows = toNumber(w.capital_and_other_usd);
    basis = "remainder";
  }
  if (opening === null || gain === null || flows === null) {
    return { value: null, basis, reason: "The ledger could not state this period in dollars." };
  }
  const average = opening + flows / 2;
  if (!(average > 0)) {
    return {
      value: null,
      basis,
      gain,
      average,
      short: flows < 0 && opening >= 0
        ? "Capital paid out was over twice opening net assets."
        : "Average capital employed was nil or below.",
      reason:
        `Average capital employed is ${money(average, { whole: true })}: opening net assets of ` +
        `${money(opening, { whole: true })} plus half of the ${money(flows, { whole: true })} of ` +
        "capital in less out. A return needs it above nil.",
    };
  }
  return { value: (gain / average) * 100, basis, gain, average, reason: null };
}

/* ── The net-assets chart ──────────────────────────────────────────── */

export const PERIODS = [
  ["30", "1M"],
  ["90", "3M"],
  ["365", "1Y"],
  ["1095", "3Y"],
  ["ALL", "All"],
];

/**
 * One row per date of the history, with what moved since the row before.
 *
 * The as-of row's marked figure is the NAV computed on this page, not the
 * snapshot the scheduler wrote at 10:15. The two differ whenever anything is
 * booked during the day — 23 September 2026 they were 434k apart, the chart
 * ending on 12.67m under a headline of 12.24m. A snapshot is history; the
 * as-of point is not history yet.
 */
export function chartRows(points, asOfDate, liveNav) {
  return (points || [])
    .filter((p) => !asOfDate || p.date <= asOfDate)
    .map((p) => {
      const m = p.since_previous || {};
      return {
        date: p.date,
        time: utc(p.date).getTime(),
        ledger: toNumber(p.carrying_value?.usd),
        marked: p.date === asOfDate && toNumber(liveNav) !== null ? toNumber(liveNav) : toNumber(p.marked),
        since: m.from ?? null,
        change: toNumber(m.change_usd),
        income: toNumber(m.net_income_usd),
        capital: toNumber(m.capital_usd),
        currency: toNumber(m.currency_translation_usd),
      };
    })
    .sort((a, b) => a.time - b.time);
}

/** The rows inside a chart period, counted back from the last row. */
export function inPeriod(rows, period) {
  if (period === "ALL" || !rows.length) return rows;
  const end = rows[rows.length - 1].time;
  return rows.filter((r) => r.time >= end - Number(period) * 86400000);
}

// A capital move is marked when it is at least this many dollars and at least
// this share of net assets either side of it. Daily currency swings on the
// krona book run to tens of thousands, and they are not capital — the marker
// is for money that came in or went out, which is what explains a jump.
export const CAPITAL_FLOOR_USD = 50000;
export const CAPITAL_SHARE = 0.02;

/**
 * The rows whose capital in less out since the previous row is material.
 * Read over the whole series, so a row's "previous" is the real previous date
 * and not the first row of whatever period is on screen.
 */
export function capitalEvents(rows) {
  const out = [];
  rows.forEach((r, i) => {
    if (r.capital === null || r.capital === undefined) return;
    const before = i > 0 ? rows[i - 1].ledger : 0;
    const scale = Math.max(Math.abs(r.ledger ?? 0), Math.abs(before ?? 0));
    if (Math.abs(r.capital) >= Math.max(CAPITAL_FLOOR_USD, CAPITAL_SHARE * scale)) out.push(r);
  });
  return out;
}

/** 1, 2, 2.5 or 5 times a power of ten: a step a reader can count in. */
export function niceStep(span, count = 4) {
  if (!(span > 0)) return 1;
  const raw = span / count;
  const magnitude = 10 ** Math.floor(Math.log10(raw));
  const f = raw / magnitude;
  const nice = f <= 1 ? 1 : f <= 2 ? 2 : f <= 2.5 ? 2.5 : f <= 5 ? 5 : 10;
  return nice * magnitude;
}

/**
 * The value axis. It starts at nil and goes below nil only as far as the data
 * does: a dip of $25,508 does not earn a gridline at minus five million, which
 * is how the old chart came to show an axis running negative on a fund that
 * was nowhere near it.
 */
export function valueAxis(values) {
  const vs = values.filter((v) => v !== null && v !== undefined && Number.isFinite(v));
  if (!vs.length) return null;
  const max = Math.max(0, ...vs);
  const min = Math.min(0, ...vs);
  const step = niceStep(max - min || 1);
  const top = Math.max(1, Math.ceil(max / step)) * step;
  const ticks = [];
  for (let i = 0; i * step <= top + step / 1000; i += 1) ticks.push(i * step);
  let bottom = 0;
  if (min < 0) {
    if (-min > step / 2) {
      const below = Math.ceil(-min / step);
      for (let i = 1; i <= below; i += 1) ticks.unshift(-i * step);
      bottom = -below * step;
    } else {
      bottom = min - step * 0.08;
    }
  }
  return { domain: [bottom, top], ticks, step, belowZero: min < 0 };
}

/* ── Comparing two valuation dates ─────────────────────────────────── */

/**
 * The holdings at the valuation date beside the same holdings at the
 * comparison date, matched on the instrument (`groupKey`: the security, its
 * currency, its type and its venue) — never on the ticker, which changes on a
 * delisting (CLAUDE.md §0.9).
 *
 * A holding absent at the comparison date was not held then, so its change is
 * its whole value; one absent now is kept, so a position that went to nil
 * between the two dates is still on the page.
 */
export function compareHoldings(current, previous) {
  const before = new Map((previous || []).map((p) => [groupKey(p), p]));
  const seen = new Set();
  const rows = (current || []).map((p) => {
    const key = groupKey(p);
    seen.add(key);
    const q = before.get(key);
    const now = usdValue(p);
    const then = q ? usdValue(q) : 0;
    return {
      ...p,
      held_now: true,
      held_then: Boolean(q),
      compare_mv_usd: q ? then : null,
      compare_quantity: q ? q.quantity : null,
      change_usd: now === null || then === null ? null : now - then,
    };
  });
  for (const [key, q] of before) {
    if (seen.has(key)) continue;
    const then = usdValue(q);
    rows.push({
      ...q,
      quantity: null, cost_basis: null, market_value: null, mv_usd: null, unrealized_pnl: null,
      last_price: null, price_date: null, face_value: null,
      held_now: false,
      held_then: true,
      compare_mv_usd: then,
      compare_quantity: q.quantity,
      change_usd: then === null ? null : -then,
    });
  }
  return rows;
}

/** A difference, or null when either side is missing. */
export const delta = (now, then) => {
  const a = toNumber(now);
  const b = toNumber(then);
  return a === null || b === null ? null : a - b;
};

/* ── Display ───────────────────────────────────────────────────────── */

/**
 * The ticker as a reader knows it. Bloomberg's " US Equity" suffix is
 * dropped for display only; the record keeps what it holds.
 */
export const displayTicker = (ticker) =>
  String(ticker || "").replace(/\s+[A-Z]{2}\s+Equity$/, "").trim();

/* ── An investor's capital account ─────────────────────────────────── */

/**
 * The capital account the Investors tab shows, out of the investor report's
 * figures.
 *
 * CLAUDE.md §0.46: every investor is a lender. Their money is a loan note to
 * Helena Partners Inc for a named deal, repaid out of that deal's proceeds, so
 * their capital account is their notes:
 *
 *   contributions   the cash they lent (A)
 *   withdrawals     the distributions declared to them (B)
 *   profit          realized on their notes: paid or re-invested, and what is
 *                   realized and not yet paid (C)
 *   ending balance  what their notes still stand at, less anything they owe
 *                   back from an over-distribution
 *
 * The notes also move when money is recycled from one deal into another, when
 * a loss is written off against them and while bookings are outstanding, and
 * none of those is a line here. So the four lines need not add to the balance,
 * and the difference is computed and shown as a difference rather than left
 * for the reader to find (§0.30: two records of one figure are both reported,
 * and the difference is the report).
 */
export function capitalAccount(kpis) {
  if (!kpis) return null;
  const contributions = toNumber(kpis.a_capital_contributions_to_date);
  const withdrawals = toNumber(kpis.b_distributions_to_date);
  const profitPaid = toNumber(kpis.profit_generated_to_date);
  const profitToPay = toNumber(kpis.c_profits_to_be_distributed);
  const profit = profitPaid === null || profitToPay === null ? null : profitPaid + profitToPay;
  const check = kpis.capital_source_crosscheck || {};
  const notes = toNumber(check.ppln_notes_outstanding);
  const owedBack = toNumber(check.owed_back_to_the_borrower) ?? 0;
  const ending = notes === null ? null : notes - owedBack;
  const parts = [contributions, withdrawals, profit, ending];
  const difference = parts.some((v) => v === null) ? null : ending - (contributions - withdrawals + profit);
  return {
    contributions,
    withdrawals,
    profit,
    profitPaid,
    profitToPay,
    unrealised: toNumber(kpis.d_nav),
    notes,
    owedBack,
    ending,
    difference,
    recycled: toNumber(kpis.recycled_between_deals),
  };
}
